/**
 * Mapa de rotas do aplicativo — a mesma tabela vale para os dois alvos.
 *
 * No web isso vira URL de verdade (`/clientes/novo`), compartilhável e
 * com o botão voltar do navegador funcionando. No desktop vira hash
 * (`#/clientes/novo`), porque o renderer é carregado por `file://` e
 * caminhos absolutos não resolvem ali.
 *
 * Cada entidade segue o mesmo desenho:
 *
 *   /<entidade>              lista
 *   /<entidade>/novo         criação
 *   /<entidade>/:id          visualização (onde existe)
 *   /<entidade>/:id/editar   edição
 *
 * Manter o padrão importa: a URL é parte da interface, e um endereço
 * previsível é o que permite salvar um atalho para "nova ordem de
 * serviço" ou mandar o link de uma OS para o colega.
 */

export const ROTAS = {
  ordens: '/ordens-servico',
  ordensNova: '/ordens-servico/nova',
  ordem: (id: string) => `/ordens-servico/${id}`,
  ordemEditar: (id: string) => `/ordens-servico/${id}/editar`,

  posVenda: '/pos-venda',

  clientes: '/clientes',
  clienteNovo: '/clientes/novo',
  clienteEditar: (id: string) => `/clientes/${id}/editar`,

  produtos: '/produtos',
  produtoNovo: '/produtos/novo',
  produtoEditar: (id: string) => `/produtos/${id}/editar`,

  estoque: '/estoque',
  estoqueEntrada: (produtoId: string) => `/estoque/${produtoId}/entrada`,
  estoqueSaida: (produtoId: string) => `/estoque/${produtoId}/saida`,

  colaboradores: '/colaboradores',
  colaboradorNovo: '/colaboradores/novo',
  colaboradorEditar: (id: string) => `/colaboradores/${id}/editar`,

  ponto: '/ponto',
  pontoBatidaNova: '/ponto/batidas/nova',
  pontoBatidaEditar: (id: string) => `/ponto/batidas/${id}/editar`,

  servicos: '/servicos',
  servicoNovo: '/servicos/novo',
  servicoEditar: (id: string) => `/servicos/${id}/editar`,

  financeiro: '/financeiro',
  financeiroNovo: '/financeiro/novo',
  financeiroDetalhe: (id: string) => `/financeiro/${id}`,
  financeiroEditar: (id: string) => `/financeiro/${id}/editar`,
  financeiroBaixar: (id: string) => `/financeiro/${id}/baixar`,

  relatorios: '/relatorios',

  // PDV: o menu leva para a venda; caixa e histórico são sub-rotas dentro
  // do modo tela cheia.
  pdv: '/pdv',
  pdvCaixa: '/pdv/caixa',
  pdvVendas: '/pdv/vendas',
  pdvDevolucao: '/pdv/devolucao',
  pdvRelatorio: '/pdv/relatorio',
  pdvFiado: '/pdv/fiado',

  backup: '/backup',
  configuracoes: '/configuracoes',
  limpeza: '/configuracoes/limpar-dados',
  maquinas: '/conta/maquinas',
  licenca: '/conta/assinatura',
} as const;

/** Abas do menu lateral, na ordem em que aparecem. */
export type IdAba =
  | 'ordens'
  | 'posVenda'
  | 'clientes'
  | 'produtos'
  | 'estoque'
  | 'colaboradores'
  | 'ponto'
  | 'servicos'
  | 'pdv'
  | 'financeiro'
  | 'relatorios'
  | 'backup'
  | 'configuracoes';

/**
 * Prefixo de cada aba. Serve para marcar o item ativo no menu mesmo
 * quando a URL está numa sub-rota (`/clientes/novo` acende "Clientes").
 */
export const PREFIXO_ABA: Record<IdAba, string> = {
  ordens: ROTAS.ordens,
  posVenda: ROTAS.posVenda,
  clientes: ROTAS.clientes,
  produtos: ROTAS.produtos,
  estoque: ROTAS.estoque,
  colaboradores: ROTAS.colaboradores,
  ponto: ROTAS.ponto,
  servicos: ROTAS.servicos,
  pdv: ROTAS.pdv,
  financeiro: ROTAS.financeiro,
  relatorios: ROTAS.relatorios,
  backup: ROTAS.backup,
  configuracoes: ROTAS.configuracoes,
};

/** Qual aba está ativa para um caminho — o prefixo mais longo vence. */
export function abaDoCaminho(caminho: string): IdAba | null {
  let melhor: IdAba | null = null;
  let tamanho = -1;
  for (const [aba, prefixo] of Object.entries(PREFIXO_ABA) as Array<[IdAba, string]>) {
    if ((caminho === prefixo || caminho.startsWith(`${prefixo}/`)) && prefixo.length > tamanho) {
      melhor = aba;
      tamanho = prefixo.length;
    }
  }
  return melhor;
}
